import { z } from "zod";

export interface TagParts {
    key: string;
    value: string;
}

// fileTags format: "tagId1#tagId2#tagId3"
export const FILE_TAGS_DELIMITER = "#";
export const KEY_VALUE_DELIMITER = ":";

export const tagSchema = z.object({
    key: z
        .string()
        .min(1, "Key is required")
        .max(64, "Key must be less than 64 characters")
        .refine((val) => /^[a-zA-Z0-9_-]+(?: [a-zA-Z0-9_-]+)*$/.test(val), {
            message:
                "Value can contain letters, numbers, underscores, hyphens, and single spaces between words",
        })
        .transform((val) => val.trim()),
    value: z
        .string()
        .min(1, "Value is required")
        .max(64, "Value must be less than 64 characters")
        .refine((val) => /^[a-zA-Z0-9_-]+(?: [a-zA-Z0-9_-]+)*$/.test(val), {
            message:
                "Value can contain letters, numbers, underscores, hyphens, and single spaces between words",
        })
        .transform((val) => val.trim()),
});

// Parse a keyValue string into separate key and value
export function parseKeyValue(keyValue: string): TagParts | null {
    const parts = keyValue.split(KEY_VALUE_DELIMITER);
    if (parts.length !== 2) return null;

    return {
        key: parts[0],
        value: parts[1],
    };
}

// Create a keyValue string from key and value
export function serializeKeyValue(key: string, value: string): string {
    return `${key}${KEY_VALUE_DELIMITER}${value}`;
}

export type TagMap = Record<string, string[]>;

export function buildTagMap(tags: TagParts[]): TagMap {
    const map: TagMap = {};
    tags.forEach(({ key, value }) => {
        map[key] = map[key] || [];
        if (!map[key].includes(value)) map[key].push(value);
    });

    // Sort keys and values
    return Object.keys(map)
        .sort()
        .reduce((sorted, key) => {
            sorted[key] = map[key].sort();
            return sorted;
        }, {} as TagMap);
}

export function getSortedKeys(tagMap: TagMap): string[] {
    return Object.keys(tagMap).sort();
}

// Parse fileTags (tagId1#tagId2#tagId3) into TagParts array
export function parseFileTags(
    fileTags: string,
    tagIdMap: Record<string, string>
): TagParts[] {
    if (!fileTags) return [];
    const tagIds = fileTags.split(FILE_TAGS_DELIMITER).filter(Boolean);
    const keyValues = tagIds.map((id) => tagIdMap[id]).filter(Boolean);
    return keyValues.map((kv) => {
        const parts = parseKeyValue(kv);
        if (!parts) throw new Error(`Invalid keyValue: ${kv}`);
        return parts;
    });
}

// Serialize TagParts array into fileTags (tagId1#tagId2#tagId3)
export function serializeFileTags(
    tagParts: TagParts[],
    allTags: Array<{ tagId: string; keyValue: string }>
): string {
    const keyValues = tagParts.map((tag) => serializeKeyValue(tag.key, tag.value));
    const tagIds = keyValues
        .map((kv) => allTags.find((t) => t.keyValue === kv)?.tagId)
        .filter(Boolean);
    return tagIds.join(FILE_TAGS_DELIMITER);
}
